// Mirrors CLIVE's follow-up reason codes in ClinicalVerificationServiceModel followupAction.smithy.
// CLIVE derives the category from the code, so the request only carries the code.

export enum FollowUpCategory {
  CLINICAL_ISSUE = "Clinical issue",
  CONFIRM_INFORMATION = "Confirm information",
  MISSING_INFORMATION = "Missing information",
  REQUEST_OUTREACH = "Request outreach",
  OTHER_REASON = "Other",
}

export interface IFollowUpReason {
  code: string;
  label: string;
  category: FollowUpCategory;
  // Dropdown group; differs from category only where CCS shows a code under another heading.
  group: FollowUpCategory;
  // CCS: "This reason will use automated prescriber outreach".
  automatedPrescriberOutreach: boolean;
}

const AUTO = true;

const reasons = (category: FollowUpCategory, entries: [string, string, boolean?][]): IFollowUpReason[] =>
  entries.map(([code, label, automated]) => ({
    code, label, category, group: category, automatedPrescriberOutreach: !!automated,
  }));

// Labels and outreach flags match the CCS follow-up modal (ClinicalCheckingWebsite followup/config.tsx).
export const FOLLOW_UP_REASONS: IFollowUpReason[] = [
  ...reasons(FollowUpCategory.CLINICAL_ISSUE, [
    ["VERIFY_DUPLICATE_DRUG_THERAPY", "Verify potential duplicate drug therapy", AUTO],
    ["VERIFY_CLINICALLY_SIGNIFICANT_INTERACTIONS", "Verify clinically significant interactions", AUTO],
    ["VERIFY_HIGH_RISK_NTI_MEDICATIONS", "Verify issues with high-risk NTI medications"],
    ["VERIFY_DOSING", "Verify dosing", AUTO],
    ["VERIFY_PREGNANCY_SAFETY", "Verify if safe for pregnancy", AUTO],
    ["POSSIBLE_MRE", "Possible MRE"],
  ]),
  ...reasons(FollowUpCategory.CONFIRM_INFORMATION, [
    ["VERIFY_DIRECTIONS", "Verify directions", AUTO],
    ["VERIFY_DRUG_DETAILS", "Verify drug strength, quantity, or formulation", AUTO],
    ["VERIFY_MEDICATION_REPLACES_PREVIOUS", "Verify if this medication replaces one previously on file", AUTO],
    ["CONFIRM_DAW_CODE", "Confirm DAW code", AUTO],
    ["CONFIRM_PREVIOUS_NDCS_DISPENSED", "Confirm previous NDCs dispensed"],
    ["VERIFY_ALLERGIES_TOLERANCE_TO_MEDICATION", "Verify allergies/tolerance to medication", AUTO],
    ["VERIFY_CUSTOMER_DEMOGRAPHICS_NAME_DOB_ADDRESS", "Verify customer demographics, name, DOB, address", AUTO],
    ["VERIFY_MD_PRESCRIBING_MEDICATION", "Verify which MD should be prescribing medication"],
    ["VERIFY_WRITTEN_DATE", "Verify written date", AUTO],
    ["PRESCRIPTION_VALIDATION_REQUIRED", "Prescription validation required"],
  ]),
  ...reasons(FollowUpCategory.MISSING_INFORMATION, [
    ["MISSING_DATE_WRITTEN", "Missing date written"],
    ["MISSING_INFORMATION_ON_TRANSFER", "Missing information on transfer"],
    ["MISSING_MEDICATION_STRENGTH_DIRECTIONS_OR_FORMULATION", "Missing medication strength, directions, or formulation"],
    ["MISSING_PATIENT_INFORMATION", "Missing patient information"],
    ["MISSING_PRESCRIBER_INFORMATION", "Missing prescriber information"],
    ["MISSING_QUANTITY", "Missing quantity"],
    ["MISSING_REFILLS", "Missing refills"],
    ["MISSING_WET_SIGNATURE", "Missing signature/confirming wet signature"],
    ["MISSING_ALLERGIES_OR_CONDITIONS", "Missing allergies or conditions"],
  ]),
  ...reasons(FollowUpCategory.REQUEST_OUTREACH, [
    ["DUPLICATE_REQUEST_IN_FOLLOW_UP", "Duplicate request in follow up"],
    ["ALTERNATE_DAY_SUPPLY", "Request alternate day supply"],
    ["ALTERNATE_MEDICATION_DUE_TO_UNAVAILABILITY_RECALL", "Request for alternate medication due to unavailability, recall, etc."],
    ["MAX_DAILY_DOSE", "Request max daily dose"],
    ["CONTROLLED_MEDICATION_REQUIRES_E_SCRIBE", "Controlled medication requires e-scribe"],
    ["ALTERNATE_QUANTITY_AUTHORIZED", "Request alternate quantity authorized"],
  ]),
  ...reasons(FollowUpCategory.OTHER_REASON, [
    ["OTHER_FOLLOW_UP_QUESTION", "Other reason"],
  ]),
  // CCS offers this as "Other reason" with "Send to a pharmacist" ticked.
  {
    code: "OTHER_CLINICAL_ISSUE",
    label: "Other reason (send to pharmacist)",
    category: FollowUpCategory.CLINICAL_ISSUE,
    group: FollowUpCategory.OTHER_REASON,
    automatedPrescriberOutreach: false,
  },
];

const REASONS_BY_CODE = new Map(FOLLOW_UP_REASONS.map((reason) => [reason.code, reason]));

export const findFollowUpReason = (code: string): IFollowUpReason | undefined => REASONS_BY_CODE.get(code);

// Same limits the backend and CLIVE enforce, checked here so the user sees them in the form.
export const MAX_FOLLOW_UP_ISSUES = 10;
export const MAX_FOLLOW_UP_QUESTION_LENGTH = 2000;
const FORBIDDEN_QUESTION_CHARACTERS = /[<>]/;

export interface IFollowUpIssueInput {
  reasonCode: string;
  followupQuestion: string;
}

/** An issue row in the form; rowId gives React a stable key when rows are removed. */
export interface IFollowUpIssueRow extends IFollowUpIssueInput {
  rowId: number;
}

let nextRowId = 0;
export const emptyFollowUpIssue = (): IFollowUpIssueRow => ({ rowId: nextRowId++, reasonCode: "", followupQuestion: "" });

/** Returns the first problem with the issues, or null when they can be submitted. */
export const validateFollowUpIssues = (issues: IFollowUpIssueInput[]): string | null => {
  if (issues.length === 0 || issues.length > MAX_FOLLOW_UP_ISSUES) {
    return `Add between 1 and ${MAX_FOLLOW_UP_ISSUES} follow-up issues.`;
  }
  const seen = new Set<string>();
  for (let i = 0; i < issues.length; i++) {
    const issue = issues[i];
    const position = `Issue ${i + 1}`;
    if (!findFollowUpReason(issue.reasonCode)) {
      return `${position}: select a reason.`;
    }
    if (seen.has(issue.reasonCode)) {
      return `${position}: "${findFollowUpReason(issue.reasonCode)!.label}" is already used.`;
    }
    seen.add(issue.reasonCode);
    const question = issue.followupQuestion.trim();
    if (!question) {
      return `${position}: enter a follow-up question.`;
    }
    if (question.length > MAX_FOLLOW_UP_QUESTION_LENGTH) {
      return `${position}: the question must be at most ${MAX_FOLLOW_UP_QUESTION_LENGTH} characters.`;
    }
    if (FORBIDDEN_QUESTION_CHARACTERS.test(question)) {
      return `${position}: the question must not contain '<' or '>'.`;
    }
  }
  return null;
};

export const toFollowUpRequestIssues = (issues: IFollowUpIssueInput[]): IFollowUpIssueInput[] =>
  issues.map((issue) => ({ reasonCode: issue.reasonCode, followupQuestion: issue.followupQuestion.trim() }));
